/**
 * ItemQuantiveSyncPanel
 *
 * Allows a user to attach a priorities.ai item to a Quantive Objective or Key Result.
 * Writes the mapping to entity_external_ids so the sync pipeline can track it.
 *
 * Displayed in the Classification tab of ItemDetailPage when Quantive is connected.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ExternalLink,
  Link2,
  Link2Off,
  Loader2,
  Search,
  X,
  ChevronDown,
  AlertCircle,
  Check,
  Target,
  TrendingUp,
} from 'lucide-react';
import { getIntegrationsApi, type QuantiveSession, type QuantiveOkrItem } from '../../../lib/integrationsApi';
import { registerExternalId, getExternalIds } from '../../../lib/connectors';
import { getCurrentWorkspaceId, getSafeSupabaseClient } from '../../../lib/supabaseSync';

// ── Quantive icon ─────────────────────────────────────────────────────────────

const QuantiveIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    <rect width="24" height="24" rx="4" fill="#0C2340" />
    <circle cx="12" cy="11" r="5" stroke="#00C4CC" strokeWidth="2" />
    <path d="M15.5 14.5L18.5 17.5" stroke="#00C4CC" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="11" r="2" fill="#00C4CC" />
  </svg>
);

// ── OKR type badge ────────────────────────────────────────────────────────────

function OkrTypeBadge({ type }: { type: 'objective' | 'key_result' }) {
  return type === 'objective' ? (
    <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-sec)]">
      <Target className="h-2.5 w-2.5" />
      Objective
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">
      <TrendingUp className="h-2.5 w-2.5" />
      Key Result
    </span>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface ExistingLink {
  connectorType: string;
  externalId: string;
  externalUrl: string | null;
  lastSyncedAt: string | null;
  mappingId?: string;
}

// ── Main component ────────────────────────────────────────────────────────────

interface ItemQuantiveSyncPanelProps {
  itemId: string;
  itemTitle?: string;
}

export function ItemQuantiveSyncPanel({ itemId, itemTitle }: ItemQuantiveSyncPanelProps) {
  const workspaceId = getCurrentWorkspaceId();

  // ── Existing links ───────────────────────────────────────────────────────
  const [links, setLinks] = useState<ExistingLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(true);

  // ── Panel state ──────────────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);

  // ── Session filter ───────────────────────────────────────────────────────
  const [sessions, setSessions] = useState<QuantiveSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');

  // ── OKR type filter ──────────────────────────────────────────────────────
  const [okrTypes, setOkrTypes] = useState<('objective' | 'key_result')[]>(['objective', 'key_result']);

  // ── Search ───────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [okrs, setOkrs] = useState<QuantiveOkrItem[]>([]);
  const [okrsLoading, setOkrsLoading] = useState(false);
  const [selectedOkr, setSelectedOkr] = useState<QuantiveOkrItem | null>(null);

  // ── Save state ───────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quantiveConnected, setQuantiveConnected] = useState<boolean | null>(null);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load existing Quantive links for this item
  const loadLinks = useCallback(async () => {
    if (!workspaceId) return;
    setLinksLoading(true);
    try {
      const allLinks = await getExternalIds(workspaceId, 'item', itemId);
      const qLinks = allLinks.filter((l) => l.connectorType === 'quantive');

      const supabase = getSafeSupabaseClient();
      if (supabase && qLinks.length > 0) {
        const { data } = await supabase
          .from('entity_external_ids')
          .select('id, external_id')
          .eq('workspace_id', workspaceId)
          .eq('entity_type', 'item')
          .eq('internal_id', itemId)
          .eq('connector_type', 'quantive')
          .eq('is_active', true);

        const idMap: Record<string, string> = {};
        for (const row of data ?? []) {
          idMap[row.external_id as string] = row.id as string;
        }
        setLinks(qLinks.map((l) => ({ ...l, mappingId: idMap[l.externalId] })));
      } else {
        setLinks(qLinks);
      }
    } finally {
      setLinksLoading(false);
    }
  }, [workspaceId, itemId]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  // When panel opens, check connection and load sessions
  useEffect(() => {
    if (!isOpen || !workspaceId) return;

    getIntegrationsApi(workspaceId)
      .getQuantiveStatus()
      .then((status) => {
        setQuantiveConnected(status.connected);
        if (!status.connected) return;

        getIntegrationsApi(workspaceId)
          .listQuantiveSessions()
          .then((s) => {
            setSessions(s);
          })
          .catch(() => {/* sessions optional */});
      })
      .catch(() => setQuantiveConnected(false));
  }, [isOpen, workspaceId]);

  // Search OKRs with debounce
  useEffect(() => {
    if (!isOpen || !quantiveConnected) return;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setOkrsLoading(true);
      setError(null);
      getIntegrationsApi(workspaceId ?? 'default')
        .searchQuantiveOkrs({
          sessionId: selectedSessionId || undefined,
          types: okrTypes,
          search: searchQuery,
          limit: 30,
        })
        .then((results) => setOkrs(results))
        .catch((err) => setError(err instanceof Error ? err.message : 'Failed to search OKRs'))
        .finally(() => setOkrsLoading(false));
    }, 350);

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [isOpen, quantiveConnected, selectedSessionId, searchQuery, okrTypes, workspaceId]);

  const handleOpen = () => {
    setIsOpen(true);
    setSelectedOkr(null);
    setSearchQuery('');
    setError(null);
    setSaveSuccess(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedOkr(null);
    setSearchQuery('');
    setError(null);
  };

  const handleSave = async () => {
    if (!selectedOkr || !workspaceId) return;
    setSaving(true);
    setError(null);
    try {
      await registerExternalId({
        workspaceId,
        entityType: 'item',
        internalId: itemId,
        connectorType: 'quantive',
        externalId: selectedOkr.id,
        externalUrl: selectedOkr.url,
      });
      setSaveSuccess(true);
      await loadLinks();
      setTimeout(() => {
        setSaveSuccess(false);
        handleClose();
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save link');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (mappingId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    await supabase
      .from('entity_external_ids')
      .update({ is_active: false })
      .eq('id', mappingId);
    await loadLinks();
  };

  const toggleOkrType = (type: 'objective' | 'key_result') => {
    setOkrTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="pt-3 border-t border-[var(--rule)]">
      {/* Section header */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide flex items-center gap-1.5">
          <QuantiveIcon className="w-3.5 h-3.5" />
          Quantive OKR
        </p>
        {!linksLoading && (
          <button
            onClick={handleOpen}
            className="flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors"
          >
            <Link2 className="h-3 w-3" />
            {links.length > 0 ? 'Add another' : 'Link to OKR'}
          </button>
        )}
      </div>

      {/* Existing links */}
      {linksLoading ? (
        <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] py-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading…
        </div>
      ) : links.length === 0 ? (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] italic py-0.5">
          Not linked to a Quantive OKR
        </p>
      ) : (
        <div className="space-y-1.5">
          {links.map((link) => (
            <div
              key={link.externalId}
              className="flex items-center gap-2 group px-2 py-1.5 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/40 border border-[var(--rule)]"
            >
              <QuantiveIcon className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="text-xs font-mono font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] truncate flex-1">
                {link.externalId}
              </span>
              {link.externalUrl && (
                <a
                  href={link.externalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors flex-shrink-0"
                  title="Open in Quantive"
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {link.mappingId && (
                <button
                  onClick={() => handleUnlink(link.mappingId!)}
                  className="text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                  title="Remove link"
                >
                  <Link2Off className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Inline link panel */}
      {isOpen && (
        <div className="mt-3 border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-sm overflow-hidden">
          {/* Panel header */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50">
            <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] flex items-center gap-1.5">
              <Link2 className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              Link to Quantive OKR
            </span>
            <button
              onClick={handleClose}
              className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="p-3 space-y-3">
            {/* Not connected */}
            {quantiveConnected === false && (
              <div className="flex items-start gap-2 p-2.5 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)]">
                <AlertCircle className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                  Quantive is not connected. Go to{' '}
                  <a href="/prioritization/settings/connectors" className="underline font-medium">
                    Settings → Connectors
                  </a>{' '}
                  to connect your Quantive account.
                </p>
              </div>
            )}

            {quantiveConnected !== false && (
              <>
                {/* Session filter */}
                {sessions.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                      Planning session (optional)
                    </label>
                    <div className="relative">
                      <select
                        value={selectedSessionId}
                        onChange={(e) => {
                          setSelectedSessionId(e.target.value);
                          setSelectedOkr(null);
                        }}
                        className="w-full appearance-none pl-3 pr-8 py-2 text-xs border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100"
                      >
                        <option value="">All sessions</option>
                        {sessions.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.title}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)] pointer-events-none" />
                    </div>
                  </div>
                )}

                {/* OKR type filter */}
                <div>
                  <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                    Type
                  </label>
                  <div className="flex gap-1.5">
                    {(['objective', 'key_result'] as const).map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => toggleOkrType(type)}
                        className={`flex items-center gap-1 px-2.5 py-1 text-xs  border font-medium transition-colors ${
                          okrTypes.includes(type)
                            ? type === 'objective'
                              ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] dark:border-[var(--slate)] dark:bg-[var(--slate)]/30'
                              : 'border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:border-[var(--green)] dark:bg-[var(--green)]/30 dark:text-[var(--text-ter)]'
                            : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-ter)] dark:bg-transparent'
                        }`}
                      >
                        {type === 'objective' ? <Target className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                        {type === 'objective' ? 'Objective' : 'Key Result'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Search */}
                <div>
                  <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                    Search OKRs
                  </label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={itemTitle ? `e.g. "${itemTitle.slice(0, 24)}"` : 'Search by name…'}
                      className="w-full pl-8 pr-3 py-2 text-xs border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100"
                    />
                    {okrsLoading && (
                      <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)] animate-spin" />
                    )}
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-1.5 text-xs text-[var(--coral)] dark:text-[var(--coral)] p-2 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    {error}
                  </div>
                )}

                {/* No results */}
                {!okrsLoading && okrs.length === 0 && !error && (
                  <p className="text-xs text-[var(--text-ter)] italic text-center py-2">
                    {searchQuery ? 'No OKRs match your search' : 'Type to search OKRs…'}
                  </p>
                )}

                {/* OKR list */}
                {okrs.length > 0 && (
                  <div className="border border-[var(--rule)] overflow-hidden max-h-52 overflow-y-auto">
                    {okrs.map((okr) => {
                      const isSelected = selectedOkr?.id === okr.id;
                      return (
                        <button
                          key={okr.id}
                          type="button"
                          onClick={() => setSelectedOkr(isSelected ? null : okr)}
                          className={`w-full flex items-start gap-2 px-3 py-2 text-left text-xs border-b border-[var(--rule)] last:border-0 transition-colors ${
                            isSelected
                              ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20'
                              : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/30'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                              <OkrTypeBadge type={okr.type} />
                              {okr.parentName && (
                                <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)] truncate max-w-[10rem]">
                                  {okr.parentName}
                                </span>
                              )}
                            </div>
                            <p className="text-[var(--text-sec)] dark:text-[var(--text-sec)] truncate leading-tight font-medium">
                              {okr.name}
                            </p>
                          </div>
                          {isSelected && (
                            <Check className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Save / Cancel */}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-3 py-1.5 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!selectedOkr || saving || saveSuccess}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[var(--slate)] hover:bg-[var(--slate)] text-white"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Saving…
                      </>
                    ) : saveSuccess ? (
                      <>
                        <Check className="h-3 w-3" />
                        Linked!
                      </>
                    ) : (
                      <>
                        <Link2 className="h-3 w-3" />
                        {selectedOkr ? `Link ${selectedOkr.type === 'objective' ? 'Objective' : 'Key Result'}` : 'Link OKR'}
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
